import React from "react";

const REF_LINE = /^\s*((?:RTS|FC|RM)-[\w-]+)\s*$/i;
const DATE_LINE = /^\s*(?:\d{4}\s+)?(\d{1,2}[./-]\d{1,2}[./-]\d{4})\s*$/;
const RATE_LINE = /\$\s*[\d,]+(?:\.\d{2})?/;
const MONTH_SECTION =
  /^(January|February|March|April|May|June|July|August|September|October|November|December)$/i;
const SUBJECT_LINE = /^[A-Z0-9][A-Z0-9\s/,&\-–—:()]+$/;

export type PastedEmailBlock =
  | { kind: "subject"; text: string }
  | { kind: "section"; text: string }
  | { kind: "vacancy"; lines: string[]; ref?: string }
  | { kind: "table-row"; cells: string[] }
  | { kind: "table"; rows: string[][] }
  | { kind: "note"; text: string }
  | { kind: "blank" };

function classifyLine(line: string): string {
  const trimmed = line.trim();
  if (!trimmed) return "blank";
  if (REF_LINE.test(trimmed)) return "ref";
  if (DATE_LINE.test(trimmed)) return "date";
  if (RATE_LINE.test(trimmed)) return "rate";
  if (MONTH_SECTION.test(trimmed)) return "section";
  if (trimmed.includes("\t")) return "tab-row";
  if (/\s{2,}/.test(trimmed) && trimmed.split(/\s{2,}/).length >= 2) return "tab-row";
  if (/^(vacancy|maximum daily rate|rts #|facility|dept|position|contract)/i.test(trimmed))
    return "header";
  return "text";
}

function splitTableCells(line: string): string[] {
  if (line.includes("\t")) {
    return line.split("\t").map((c) => c.trim()).filter(Boolean);
  }
  if (/\s{2,}/.test(line)) {
    return line.split(/\s{2,}/).map((c) => c.trim()).filter(Boolean);
  }
  return [line.trim()].filter(Boolean);
}

/** Strip Gmail/Outlook HTML to safe email body for display. */
export function sanitizeEmailHtml(rawHtml: string): string {
  if (!rawHtml.trim()) return "";
  try {
    const doc = new DOMParser().parseFromString(rawHtml, "text/html");
    doc.querySelectorAll("script, style, meta, link, iframe, object, embed").forEach((el) => {
      el.remove();
    });
    doc.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on") || name === "srcdoc") {
          el.removeAttribute(attr.name);
        }
      });
    });
    const body = doc.body;
    return body?.innerHTML?.trim() || "";
  } catch {
    return "";
  }
}

export function extractEmailPasteFromClipboard(data: DataTransfer | null | undefined): {
  text: string;
  html: string;
} {
  if (!data) return { text: "", html: "" };
  const text = data.getData("text/plain")?.trim() || "";
  const html = sanitizeEmailHtml(data.getData("text/html") || "");
  return { text, html };
}

/** Read Gmail/HTML email from system clipboard (requires user click). */
export async function readEmailFromSystemClipboard(): Promise<{
  text: string;
  html: string;
}> {
  if (typeof navigator === "undefined" || !navigator.clipboard) {
    return { text: "", html: "" };
  }

  if (typeof navigator.clipboard.read === "function") {
    try {
      const items = await navigator.clipboard.read();
      let text = "";
      let html = "";
      for (const item of items) {
        if (item.types.includes("text/plain")) {
          const blob = await item.getType("text/plain");
          text = (await blob.text()).trim();
        }
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          html = sanitizeEmailHtml(await blob.text());
        }
      }
      if (text || html) return { text, html };
    } catch {
      // fall through to readText
    }
  }

  try {
    const text = (await navigator.clipboard.readText()).trim();
    return { text, html: "" };
  } catch {
    return { text: "", html: "" };
  }
}

function collapseTableRows(blocks: PastedEmailBlock[]): PastedEmailBlock[] {
  const out: PastedEmailBlock[] = [];
  let tableBuffer: string[][] = [];

  const flushTable = () => {
    if (tableBuffer.length > 0) {
      out.push({ kind: "table", rows: tableBuffer });
      tableBuffer = [];
    }
  };

  for (const block of blocks) {
    if (block.kind === "table-row") {
      tableBuffer.push(block.cells);
      continue;
    }
    flushTable();
    out.push(block);
  }
  flushTable();
  return out;
}

/** Group pasted email text into blocks for email-like preview. */
export function parsePastedEmailBlocks(text: string): PastedEmailBlock[] {
  const lines = text.split(/\r?\n/);
  const blocks: PastedEmailBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) {
      blocks.push({ kind: "blank" });
      i += 1;
      continue;
    }

    if (blocks.length === 0 && SUBJECT_LINE.test(trimmed) && trimmed.length < 120) {
      blocks.push({ kind: "subject", text: trimmed });
      i += 1;
      continue;
    }

    if (MONTH_SECTION.test(trimmed)) {
      blocks.push({ kind: "section", text: trimmed });
      i += 1;
      continue;
    }

    const cells = splitTableCells(trimmed);
    if (cells.length >= 2) {
      blocks.push({ kind: "table-row", cells });
      i += 1;
      continue;
    }

    if (REF_LINE.test(trimmed)) {
      const chunk: string[] = [];
      const ref = trimmed;
      let j = Math.max(0, i - 2);
      while (j < i) {
        if (lines[j].trim()) chunk.push(lines[j].trim());
        j += 1;
      }
      chunk.push(trimmed);
      j = i + 1;
      while (j < lines.length && j < i + 8) {
        const next = lines[j].trim();
        if (!next) break;
        if (REF_LINE.test(next) && j > i) break;
        if (MONTH_SECTION.test(next)) break;
        chunk.push(next);
        j += 1;
      }
      blocks.push({ kind: "vacancy", lines: chunk, ref });
      i = j;
      continue;
    }

    if (/^(required documentation|locum allowances|please note|for more information)/i.test(trimmed)) {
      const noteLines = [trimmed];
      i += 1;
      while (
        i < lines.length &&
        lines[i].trim() &&
        !REF_LINE.test(lines[i].trim()) &&
        !MONTH_SECTION.test(lines[i].trim())
      ) {
        noteLines.push(lines[i].trim());
        i += 1;
      }
      blocks.push({ kind: "note", text: noteLines.join("\n") });
      continue;
    }

    blocks.push({ kind: "note", text: trimmed });
    i += 1;
  }

  return collapseTableRows(blocks);
}

function EmailHtmlBody({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={`email-original-html px-4 py-3 text-sm text-gray-800 bg-white overflow-y-auto custom-scrollbar ${className ?? "h-full min-h-0"}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function PlainTextEmailBody({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <pre
      className={`px-4 py-3 text-[13px] leading-relaxed text-gray-900 bg-white overflow-y-auto custom-scrollbar whitespace-pre-wrap break-words font-sans ${className ?? "h-full min-h-0"}`}
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      {text}
    </pre>
  );
}

export function PastedEmailPreview({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const blocks = React.useMemo(() => parsePastedEmailBlocks(text), [text]);

  if (!text.trim()) {
    return (
      <div
        className={`px-4 py-8 text-center text-sm text-gray-400 flex items-center justify-center ${className ?? "h-full min-h-0"}`}
      >
        Paste an email here (Ctrl+V) to see rows and tables
      </div>
    );
  }

  return (
    <div
      className={`px-4 py-3 text-sm text-gray-800 bg-white overflow-y-auto custom-scrollbar ${className ?? "h-full min-h-0"}`}
    >
      {blocks.map((block, index) => {
        if (block.kind === "blank") {
          return <div key={`b-${index}`} className="h-2" />;
        }
        if (block.kind === "subject") {
          return (
            <div
              key={`b-${index}`}
              className="font-semibold text-base text-gray-900 border-b border-gray-100 pb-2 mb-3"
            >
              {block.text}
            </div>
          );
        }
        if (block.kind === "section") {
          return (
            <div
              key={`b-${index}`}
              className="text-xs font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 px-2 py-1 rounded mt-3 mb-2"
            >
              {block.text}
            </div>
          );
        }
        if (block.kind === "table") {
          return (
            <div key={`b-${index}`} className="overflow-x-auto mb-3 rounded-lg border border-gray-200">
              <table className="w-full text-xs border-collapse">
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri} className={ri === 0 ? "bg-gray-50 font-semibold" : "border-t border-gray-100"}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-2 py-1.5 align-top border-r border-gray-100 last:border-r-0">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.kind === "table-row") {
          return (
            <div
              key={`b-${index}`}
              className="grid gap-1 text-xs border-b border-gray-100 py-1.5"
              style={{
                gridTemplateColumns: `repeat(${Math.max(block.cells.length, 1)}, minmax(0, 1fr))`,
              }}
            >
              {block.cells.map((cell, ci) => (
                <span key={ci} className="truncate" title={cell}>
                  {cell}
                </span>
              ))}
            </div>
          );
        }
        if (block.kind === "vacancy") {
          return (
            <div
              key={`b-${index}`}
              className="rounded-lg border border-emerald-100 bg-emerald-50/40 px-3 py-2 mb-2 space-y-0.5"
            >
              {block.lines.map((line, li) => {
                const kind = classifyLine(line);
                const className =
                  kind === "ref"
                    ? "font-mono text-xs font-semibold text-blue-700"
                    : kind === "rate"
                      ? "font-semibold text-emerald-800"
                      : kind === "date"
                        ? "text-purple-800 font-medium"
                        : kind === "header"
                          ? "text-[10px] uppercase text-gray-500 font-bold"
                          : "text-gray-700";
                return (
                  <div key={li} className={className}>
                    {line}
                  </div>
                );
              })}
            </div>
          );
        }
        return (
          <p
            key={`b-${index}`}
            className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap mb-2"
          >
            {block.text}
          </p>
        );
      })}
    </div>
  );
}

/** Original email view: raw HTML when available, exact plain text otherwise (no re-layout). */
export function PastedEmailDisplay({
  text,
  html,
  className,
}: {
  text: string;
  html?: string;
  className?: string;
}) {
  if (html?.trim()) {
    return (
      <EmailHtmlBody html={sanitizeEmailHtml(html)} className={className} />
    );
  }
  return <PlainTextEmailBody text={text} className={className} />;
}

const PASTED_EMAIL_DOC_STYLES = `
  body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; line-height: 1.5; color: #1f2937; margin: 16px; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 12px; }
  th, td { border: 1px solid #e5e7eb; padding: 6px 8px; vertical-align: top; text-align: left; }
  th { background: #f9fafb; font-weight: 600; }
  tr:nth-child(even) td { background: #fafafa; }
  p { margin: 0 0 8px; }
  .email-subject { font-weight: 600; font-size: 15px; border-bottom: 1px solid #f3f4f6; padding-bottom: 8px; margin-bottom: 12px; }
  .email-section { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #4338ca; background: #eef2ff; padding: 4px 8px; border-radius: 4px; margin: 12px 0 8px; }
  .email-vacancy { border: 1px solid #d1fae5; background: #ecfdf5; border-radius: 8px; padding: 8px 12px; margin-bottom: 8px; }
  .email-note { font-size: 12px; color: #4b5563; white-space: pre-wrap; margin-bottom: 8px; }
`;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Render parsed plain-text email blocks as a standalone HTML document body. */
export function pastedEmailBlocksToHtml(text: string): string {
  const blocks = parsePastedEmailBlocks(text);
  const parts: string[] = [];

  for (const block of blocks) {
    if (block.kind === "blank") {
      parts.push("<div style=\"height:8px\"></div>");
      continue;
    }
    if (block.kind === "subject") {
      parts.push(`<div class="email-subject">${escapeHtml(block.text)}</div>`);
      continue;
    }
    if (block.kind === "section") {
      parts.push(`<div class="email-section">${escapeHtml(block.text)}</div>`);
      continue;
    }
    if (block.kind === "table") {
      parts.push("<table><tbody>");
      block.rows.forEach((row, rowIndex) => {
        parts.push("<tr>");
        row.forEach((cell) => {
          const tag = rowIndex === 0 ? "th" : "td";
          parts.push(`<${tag}>${escapeHtml(cell)}</${tag}>`);
        });
        parts.push("</tr>");
      });
      parts.push("</tbody></table>");
      continue;
    }
    if (block.kind === "table-row") {
      parts.push("<tr>");
      block.cells.forEach((cell) => {
        parts.push(`<td>${escapeHtml(cell)}</td>`);
      });
      parts.push("</tr>");
      continue;
    }
    if (block.kind === "vacancy") {
      parts.push('<div class="email-vacancy">');
      block.lines.forEach((line) => {
        parts.push(`<div>${escapeHtml(line)}</div>`);
      });
      parts.push("</div>");
      continue;
    }
    parts.push(`<p class="email-note">${escapeHtml(block.text)}</p>`);
  }

  return parts.join("\n");
}

function wrapPlainTextEmailDocument(text: string, title = "Email"): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  pre {
    margin: 0;
    padding: 16px;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
    line-height: 1.45;
    color: #222222;
    background: #ffffff;
  }
</style>
</head>
<body><pre>${escapeHtml(text)}</pre></body>
</html>`;
}

function wrapOriginalEmailHtmlDocument(html: string, title = "Email"): string {
  const safe = sanitizeEmailHtml(html);
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { overflow-x: auto; }
</style>
</head>
<body>${safe}</body>
</html>`;
}

function wrapPastedEmailHtmlDocument(bodyHtml: string, title = "Pasted shift email"): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${PASTED_EMAIL_DOC_STYLES}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}

/** Build the file uploaded to AI Shifts — preserve original layout (plain pre or raw HTML). */
export function buildPastedEmailDocumentFile(text: string, html?: string): File {
  const timestamp = Date.now();
  const trimmedText = text.trim();
  const trimmedHtml = sanitizeEmailHtml(html || "");

  if (trimmedHtml) {
    const documentHtml = wrapOriginalEmailHtmlDocument(trimmedHtml);
    return new File([documentHtml], `pasted-email-${timestamp}.html`, {
      type: "text/html;charset=utf-8",
    });
  }

  if (trimmedText) {
    const documentHtml = wrapPlainTextEmailDocument(trimmedText);
    return new File([documentHtml], `pasted-email-${timestamp}.html`, {
      type: "text/html;charset=utf-8",
    });
  }

  return new File([""], `pasted-email-${timestamp}.txt`, {
    type: "text/plain;charset=utf-8",
  });
}

export function pastedEmailHasEnoughContent(text: string, html?: string): boolean {
  const trimmedText = text.trim();
  const trimmedHtml = sanitizeEmailHtml(html || "");
  if (trimmedHtml.replace(/<[^>]+>/g, "").trim().length >= 40) return true;
  return trimmedText.length >= 40;
}
